import React from "react";
import { Input, Alert, Empty } from "antd";
import { connect } from "react-redux";
import isEqual from "lodash.isequal";
import { importAllCourses } from "actions/courseActions";
import CustomCheckbox from "components/fragments/custom-checkbox/CustomCheckbox";
import EnterSubjectMarks from "components/fragments/EnterSubjectMarks";
import CustomBadge from "components/fragments/CustomBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { MAX_SUBJECTS } from "utils/generalHelpers";
import { SearchOutlined } from "@ant-design/icons";
import "assets/stylesheets/SelectCourse.scss";
import { ACTION_TYPES } from "reducers/actionTypes";

// First we check if you support touch, otherwise it's click:
let touchEvent = "ontouchstart" in window ? "touchstart" : "click";

class SelectCourse extends React.Component {
  state = {
    ALL_COURSES_BY_STATE: {},
    allSubjects: [],
    visible: false,
    search: "",
    listening: false,
  };

  componentDidMount() {
    const ALL_COURSES_BY_STATE = importAllCourses();
    this.setState({ ALL_COURSES_BY_STATE: ALL_COURSES_BY_STATE });

    document.addEventListener("keydown", this.watchEscape, false);
  }

  watchEscape = (event) => {
    if (event.key === "Escape") {
      this.setState({ visible: false });
    }
  };

  // Filtering Start ------------------------------------------------------ //
  getFilteredSubjects = () => {
    const { ALL_COURSES_BY_STATE, search } = this.state;
    const { activeState } = this.props;
    const allSubjects = ALL_COURSES_BY_STATE[activeState];
    if (!allSubjects) return [];
    if (search === "") return allSubjects;
    const filteredSubjects = allSubjects.filter((sub) => {
      const rawSearch = search.toLowerCase();
      const rawSubject = sub.subject_desc.toLowerCase();
      return rawSubject.indexOf(rawSearch) > -1;
    });
    return filteredSubjects;
  };

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
  };

  // Filtering End ------------------------------------------------------ //

  // Setting Visibility Start -------------------------------------------- //
  handleSearchClick = () => {
    const { visible } = this.state;
    if (!visible) {
      this.setState({ listening: true });
      document.addEventListener(touchEvent, this.handleOutsideClick, false);
    }
    this.setState({ visible: true });
  };
  handleOutsideClick = (event) => {
    this.setState({ listening: false });
    const { target } = event;
    if (!this.dropdownContainer || !this.dropdownAndSearch) {
      return;
    }
    if (
      this.dropdownContainer.contains(target) ||
      this.dropdownAndSearch.contains(target)
    ) {
      return;
    }
    document.removeEventListener(touchEvent, this.handleOutsideClick, false);
    this.setState({ visible: false });
  };
  handleClickClose = () => {
    this.setState({ visible: false });
    document.removeEventListener(touchEvent, this.handleOutsideClick, false);
  };

  // Setting Visibility End -------------------------------------------------- //

  // Handle Subject Selection Start -------------------------------------------- //
  handleSubjectChange = (newSub) => {
    const { selectedSubjects, hscMarks } = this.props;
    let newSelectedSubjects;
    let newHscMarks = { ...hscMarks };
    let replaceHSC = false;
    let prevSubjectIndex = null;
    const prevSubject = selectedSubjects.find((prevSub, i) => {
      if (isEqual(prevSub, newSub)) {
        prevSubjectIndex = i;
        return true;
      } else {
        return false;
      }
    });
    if (prevSubject) {
      newSelectedSubjects = Array.from(selectedSubjects);
      newSelectedSubjects.splice(prevSubjectIndex, 1);
      replaceHSC = true;
      if (newSub.subject_code in newHscMarks) {
        delete newHscMarks[newSub.subject_code];
      }
    } else {
      newSelectedSubjects = selectedSubjects.concat(newSub);
    }
    if (replaceHSC) {
      this.props.dispatch({
        type: ACTION_TYPES.subjects.update,
        data: { selectedSubjects: newSelectedSubjects, hscMarks: newHscMarks },
      });
    } else {
      this.props.dispatch({
        type: ACTION_TYPES.subjects.update,
        data: { selectedSubjects: newSelectedSubjects },
      });
    }
  };

  subjectSelected = (subject) => {
    const { selectedSubjects } = this.props;
    const foundSubject = selectedSubjects.find((sub) => {
      if (isEqual(sub, subject)) {
        return true;
      } else {
        return false;
      }
    });
    if (foundSubject) {
      return true;
    } else {
      return false;
    }
  };
  // Handle Subject Selection End -------------------------------------------- //

  renderEmptyDropdown = () => {
    return (
      <div>
        <Empty
          description={<span>No subjects match filter requirements.</span>}
        />
      </div>
    );
  };

  render() {
    const { visible } = this.state;
    const filteredSubjects = this.getFilteredSubjects();
    const { selectedSubjects, activeState } = this.props;

    const forbidSelecting = selectedSubjects.length >= MAX_SUBJECTS;

    return (
      <section className="section">
        <div className="section-heading-container">
          <h2
            className="section-card-title"
            tabIndex={0}
            aria-label={`2. Your Year 12 courses ${selectedSubjects.length} courses selected`}
          >
            2. Your Year 12 courses
          </h2>
          <CustomBadge count={selectedSubjects.length} />
        </div>
        <p>
          {activeState === "NSW"
            ? "This is a list of NSW HSC courses examined in 2025. If you completed an HSC course in a previous year and it isn’t listed here, don’t worry – just select all those that you can."
            : "This is a list of courses with NSW equivalents. If one of your courses isn’t listed, don't worry – just select all those that you can."}
        </p>
        <label
          htmlFor="select-course-search"
          style={{ fontWeight: 600, color: "#4B4E6A" }}
        >
          Select 4 to 10 courses
        </label>
        <div
          id="select-course-dropdown-and-search"
          ref={(el) => {
            this.dropdownAndSearch = el;
          }}
        >
          {forbidSelecting && (
            <div className="alert-message-container">
              <Alert
                showIcon
                type="warning"
                message="You can only select up to 10 courses."
              />
            </div>
          )}
          <Input
            prefix={<SearchOutlined style={{ fontSize: 18 }} />}
            id="select-course-search"
            style={{ marginBottom: 10 }}
            placeholder="Search"
            onChange={this.handleSearchChange}
            onPressEnter={this.handleSearchClick}
            onClick={this.handleSearchClick}
            size="large"
            autoComplete="off"
            aria-label="Search courses"
            bordered={false}
          />
          <div
            id="select-course-dropdown-container"
            className={visible ? "" : "hide"}
            ref={(dropdownContainer) => {
              this.dropdownContainer = dropdownContainer;
            }}
            tabIndex={0}
            aria-label="Select year 12 courses, press Escape Key to exit"
          >
            <div
              id="select-course-dropdown-close"
              onClick={this.handleClickClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </div>

            {filteredSubjects.length === 0 ? (
              this.renderEmptyDropdown()
            ) : (
              <div id="select-course-dropdown">
                <fieldset>
                  <legend className="sr-only">Select courses</legend>
                  {filteredSubjects.map((sub) => (
                    <div
                      style={{ display: "inline-block", width: "100%" }}
                      key={sub.subject_id + sub.subject_desc}
                    >
                      <CustomCheckbox
                        id={sub.subject_id + sub.subject_desc}
                        key={sub.subject_id + sub.subject_desc}
                        label={sub.subject_desc}
                        selected={this.subjectSelected(sub)}
                        disabled={forbidSelecting}
                        onClickCheckbox={() => {
                          this.handleSubjectChange(sub);
                        }}
                      />
                    </div>
                  ))}
                </fieldset>
              </div>
            )}
          </div>
        </div>
        <EnterSubjectMarks
          subjectSelected={this.subjectSelected}
          handleSubjectChange={this.handleSubjectChange}
        />
      </section>
    );
  }
}

// instructs to change the props of "SelectCourse" from the outside
const mapStateToProps = (store) => {
  const { activeState } = store.selectStateReducer;
  const { selectedSubjects, hscMarks } = store.selectSubjectsReducer;
  return {
    activeState: activeState,
    selectedSubjects: selectedSubjects,
    hscMarks: hscMarks,
  };
};

export default connect(mapStateToProps)(SelectCourse);
